import type { PropAsset } from "building-types/props/PropAsset";
import { builtInPropSvgUrlFromS3Location } from "common/builtInPropRuntimeS3Keys";
import { getFileStorageBaseURL } from "common/constants/fileStorageBaseUrl";
import { ensureDefined } from "common/utils/assertOfType";
import { addCacheSuffixToURL } from "common/utils/assetFetcher";
import type { SVGAssetLoader } from "doc/plans/assetLoader";
import { mat3Dot, mat3Ident, type Mat3 } from "geom/mat3";
import {
  polyLine2Close,
  polyLine2Copy,
  polyLine2IsClosed,
  polyLine2Move,
  polyLine2NonUniformScale,
  polyLine2Rotate,
  polyLine2SetCW,
} from "geom/polyLine2";
import { polyLine2New, type PolyLine2 } from "geom/polyLine2Types";
import type { Vec2 } from "geom/vec2";
import { propSvgFileNameFromName } from "gfx/propFilenames";
import { DirectPath, DirectShapePath, SVGDirectCallbacks } from "lib/house/svg/svgDirectParserCallbacks";
import { SVGParser, type SVGPathStyle } from "svg/svgParser";
import { BufferAttribute, BufferGeometry } from "three";
import { assetLoaderInstance } from "./plans/assets/assetLoaderInstance";

const svgCache: { [url: string]: string } = {};

async function fetchSVG(url: string, loader?: SVGAssetLoader): Promise<string> {
  if (svgCache[url]) {
    return svgCache[url];
  }

  let svgText: string | undefined;

  if (!loader) {
    url = addCacheSuffixToURL(url);
    const svg = await fetch(url);
    svgText = await svg.text();
  } else {
    svgText = await ensureDefined(loader.fetchText)(url);
  }

  return (svgCache[url] = svgText);
}

export interface SvgGeometry {
  lines: BufferGeometry;
  polyLines: PolyLine2[];
}

export interface ParsedSvgGeometry {
  linePolylines: PolyLine2[];
  fillPolylines: PolyLine2[];
}

function propGeometryNew(lines: BufferGeometry, polyLines: PolyLine2[]): SvgGeometry {
  return {
    lines,
    polyLines,
  };
}

function svgStyleHasVisibleFill(style: SVGPathStyle | undefined): boolean {
  if (style?.fill === "none") {
    return false;
  }

  return (style?.opacity ?? 1) > 0 && (style?.fillOpacity ?? 1) > 0;
}

function transformPointsToPolyline(points: readonly [number, number][], transform: Mat3): PolyLine2 {
  const line = polyLine2New(points.length * 2);
  for (let pointIndex = 0; pointIndex < points.length; pointIndex++) {
    const [x, y] = mat3Dot(transform, points[pointIndex][0], points[pointIndex][1]);
    line[pointIndex * 2] = x;
    line[pointIndex * 2 + 1] = y;
  }
  return line;
}

class PropSvgDirectPath extends DirectPath {
  constructor(private readonly parsedGeometry: ParsedSvgGeometry) {
    super(mat3Ident());
  }

  protected drawPolyline(points: [number, number][], transform: Mat3, style?: SVGPathStyle): void {
    if (points.length < 2) {
      return;
    }

    const line = transformPointsToPolyline(points, transform);
    const isFillCandidate = polyLine2IsClosed(line) && svgStyleHasVisibleFill(style);
    if (isFillCandidate) {
      this.parsedGeometry.fillPolylines.push(polyLine2SetCW(polyLine2Close(line)));
      return;
    }

    this.parsedGeometry.linePolylines.push(line);
  }
}

export function parsePropSvgGeometry(svgText: string): ParsedSvgGeometry {
  const parsedGeometry: ParsedSvgGeometry = {
    linePolylines: [],
    fillPolylines: [],
  };
  const callbacks = new SVGDirectCallbacks<PropSvgDirectPath>(
    new DirectShapePath<PropSvgDirectPath>(new PropSvgDirectPath(parsedGeometry)),
  );
  const svgParser = new SVGParser(callbacks);
  svgParser.parse(svgText);
  return parsedGeometry;
}

function linePolylinesToBufferGeometry(lines: readonly PolyLine2[]): BufferGeometry {
  let lineBufferSize = 0;
  for (const line of lines) {
    // Explode each polyline into individual segments, each with two XYZ vertices.
    lineBufferSize += (line.length - 2) * 3;
  }

  const lineBuffer: Float32Array = new Float32Array(lineBufferSize);
  let writeIndex = 0;
  for (const line of lines) {
    for (let seg = 0, segCount = (line.length - 2) / 2; seg < segCount; ++seg) {
      lineBuffer[writeIndex] = line[seg * 2];
      lineBuffer[writeIndex + 1] = line[seg * 2 + 1];
      lineBuffer[writeIndex + 2] = 0;
      lineBuffer[writeIndex + 3] = line[seg * 2 + 2];
      lineBuffer[writeIndex + 4] = line[seg * 2 + 3];
      lineBuffer[writeIndex + 5] = 0;
      writeIndex += 6;
    }
  }
  if (!(writeIndex === lineBuffer.length)) {
    console.assert(false);
  }

  const linesGeometry = new BufferGeometry();
  linesGeometry.setAttribute("position", new BufferAttribute(lineBuffer, 3));
  return linesGeometry;
}

type SVGGeometryByUrl = { [url: string]: Promise<SvgGeometry | null> };
type ParsedSVGGeometryByUrl = { [url: string]: Promise<ParsedSvgGeometry> };

class SvgGeometryCache {
  private readonly geometryCache: SVGGeometryByUrl = {};

  private readonly parsedGeometryCache: ParsedSVGGeometryByUrl = {};

  private async getParsedSvgGeometryInternalFromUrl({
    cacheName,
    resolvedSvgUrl,
  }: {
    cacheName: string;
    resolvedSvgUrl: string;
  }): Promise<ParsedSvgGeometry> {
    const cacheKey = `${cacheName}:${resolvedSvgUrl}`;

    if (this.parsedGeometryCache[cacheKey] !== undefined) {
      return this.parsedGeometryCache[cacheKey];
    }

    const assetLoader = await assetLoaderInstance();
    const promise = (async (): Promise<ParsedSvgGeometry> => {
      try {
        const svgText = await fetchSVG(resolvedSvgUrl, assetLoader);
        return parsePropSvgGeometry(svgText);
      } catch (e) {
        console.warn(e);
        return { linePolylines: [], fillPolylines: [] };
      }
    })();

    return (this.parsedGeometryCache[cacheKey] = promise);
  }

  private async getSvgPolylinesInternal(propAsset: PropAsset): Promise<PolyLine2[]> {
    const { name } = propAsset;
    const resolvedSvgUrl = await SvgGeometryCache.resolveSvgUrl(propAsset);
    const parsedGeometry = await this.getParsedSvgGeometryInternalFromUrl({ cacheName: name, resolvedSvgUrl });
    return parsedGeometry.linePolylines;
  }

  private async getSvgGeometryInternal(cacheName: string, resolvedSvgUrl: string): Promise<SvgGeometry | null> {
    const parsedGeometry = await this.getParsedSvgGeometryInternalFromUrl({ cacheName, resolvedSvgUrl });
    return propGeometryNew(linePolylinesToBufferGeometry(parsedGeometry.linePolylines), parsedGeometry.fillPolylines);
  }

  private async getSvgPolylines(
    propAsset: PropAsset,
    position?: Vec2,
    rotation?: number,
    scaleX?: number,
    scaleY?: number,
  ): Promise<PolyLine2[]> {
    const parsedLines = await this.getSvgPolylinesInternal(propAsset);
    const transformedLines: PolyLine2[] = [];
    for (const parsed of parsedLines) {
      const transformed = polyLine2Copy(parsed);
      polyLine2NonUniformScale(transformed, scaleX ?? 1, scaleY ?? 1);
      polyLine2Rotate(transformed, rotation ?? 0);
      polyLine2Move(transformed, position?.[0] ?? 0, position?.[1] ?? 0);
      transformedLines.push(transformed);
    }
    return transformedLines;
  }

  async getPropPolylines(
    propAsset: PropAsset,
    position?: Vec2,
    rotation?: number,
    scaleX?: number,
    scaleY?: number,
  ): Promise<PolyLine2[]> {
    return this.getSvgPolylines(propAsset, position, rotation, scaleX, scaleY);
  }

  private static async resolveSvgUrl(asset: Pick<PropAsset, "name" | "svgUrl" | "s3Location">): Promise<string> {
    const { svgUrl, s3Location, name } = asset;
    if (svgUrl) {
      return svgUrl;
    }
    if (s3Location) {
      const baseUrl = await getFileStorageBaseURL();
      if (!baseUrl) {
        throw new Error("Failed to get file storage base URL for prop 2D SVG geometry");
      }
      return `${baseUrl}${builtInPropSvgUrlFromS3Location(s3Location)}`;
    }
    return "/static/props/svg/" + propSvgFileNameFromName(name);
  }

  async getProp2DGeometry(asset: Pick<PropAsset, "name" | "svgUrl" | "s3Location">): Promise<SvgGeometry | null> {
    const name = asset.name;
    const resolvedUrl = await SvgGeometryCache.resolveSvgUrl(asset);
    const cacheKey = `${name}:${resolvedUrl}`;

    if (this.geometryCache[cacheKey] !== undefined) {
      return this.geometryCache[cacheKey];
    }

    const promise = (async (): Promise<SvgGeometry | null> => {
      try {
        return await this.getSvgGeometryInternal(name, resolvedUrl);
      } catch (e) {
        console.warn(e);
        return null;
      }
    })();

    return (this.geometryCache[cacheKey] = promise);
  }
}

export const defaultSvgCache = new SvgGeometryCache();
export const defaultPropCache = defaultSvgCache;
